import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';

export type UiStateKey =
  | 'PEND_ASSIGN'
  | 'PEND_IMPORT'
  | 'IMPORT'
  | 'IMPORT_FAILED'
  | 'TO_PUBLISH'
  | 'PUBLISH_SCHEDULED'
  | 'PUBLISHED';

export interface HistoryItem {
  /** 'Creado', 'Publicado', 'Error: …', etc */
  label: string;
  /** ISO date */
  date: string;
  /** 'created' | 'published' | 'error' | 'imported' | 'scheduled' */
  type: 'created' | 'published' | 'error' | 'imported' | 'scheduled';
  description?: string;
}

@Component({ standalone: false, selector: 'app-catalog-history-drawer',
  templateUrl: './catalog-history-drawer.component.html',
  styleUrls: ['./catalog-history-drawer.component.scss']
})
export class CatalogHistoryDrawerComponent implements OnChanges {
  @Input() visible = false;
  @Input() catalogo: any = {};
  @Input() empresasGrupos: any[] = [];
  @Input() history: HistoryItem[] = [];

  @Output() close = new EventEmitter<void>();
  @Output() retry = new EventEmitter<any>();

  trackByHistory = (index: number, item: HistoryItem): string | number => `${item?.type ?? ''}-${item?.date ?? ''}-${index}`;

  uiState: UiStateKey = 'TO_PUBLISH';

  ngOnChanges(): void {
    this.uiState = this.mapToUiState(
      { catalogo: this.catalogo, empresas_grupos: this.empresasGrupos }
    );
  }

  get title(): string {
    return `Historial detallado: ${this.catalogo?.nombre || this.catalogo?.descripcion || 'Catálogo'}`;
  }

  get statusTag(): { color: string; text: string } {
    switch (this.uiState) {
      case 'PUBLISHED':          return { color: 'green',   text: 'Publicado' };
      case 'PUBLISH_SCHEDULED':  return { color: 'blue',    text: 'Publicación Programada' };
      case 'TO_PUBLISH':         return { color: 'yellow',  text: 'Pendiente de publicar' };
      case 'IMPORT':             return { color: 'gold',    text: 'Importando' };
      case 'PEND_ASSIGN':        return { color: 'blue',    text: 'Pendiente de asignar' };
      case 'PEND_IMPORT':        return { color: 'blue',    text: 'Pendiente de importar' };
      case 'IMPORT_FAILED':      return { color: 'red',     text: 'Error' };
      default:                   return { color: 'default', text: '—' };
    }
  }

  get mainDate(): string | null {
    return (
      this.catalogo?.fecha_publicacion ??
      this.catalogo?.fecha_importacion ??
      this.catalogo?.fecha_creacion ??
      null
    );
  }

  onRetry(): void {
    this.retry.emit(this.catalogo);
  }

  // ========= Mapeo de estado (tus reglas) =========
  private mapToUiState(
    data: { catalogo: any; empresas_grupos: unknown[] },
    now: Date = new Date()
  ): UiStateKey {
    const empresas = data?.empresas_grupos ?? [];
    const { estado = '', fecha_importacion, fecha_publicacion } = data?.catalogo ?? {};

    // 1) PEND_ASSIGN => empresas_grupos con 0 elementos
    if (empresas.length === 0) return 'PEND_ASSIGN';

    const estadoUpper = String(estado).toUpperCase();

    // 2) IMPORT_FAILED => tiene fecha_importacion y estado !== 'PUBLICADO'
    if (fecha_importacion && estadoUpper !== 'PUBLICADO') return 'IMPORT_FAILED';

    // 3) IMPORT => tiene fecha_importacion
    if (fecha_importacion) return 'IMPORT';

    // 4) TO_PUBLISH => fecha_publicacion a null
    if (fecha_publicacion === null) return 'TO_PUBLISH';

    // 5) PUBLISH_SCHEDULED => fecha_publicacion > ahora
    if (fecha_publicacion) {
      const pub = new Date(fecha_publicacion);
      if (!isNaN(pub.getTime()) && pub.getTime() > now.getTime()) {
        return 'PUBLISH_SCHEDULED';
      }
      // 6) PUBLISHED => fecha_publicacion <= ahora y estado === 'PUBLICADO'
      if (!isNaN(pub.getTime()) && pub.getTime() <= now.getTime() && estadoUpper === 'PUBLICADO') {
        return 'PUBLISHED';
      }
    }

    // Caso no especificado: lo dejamos como TO_PUBLISH
    return 'TO_PUBLISH';
  }
}
